// Bulandshahr-only Institute CMS page for the 3 editorial feature blocks
// rendered on the public Home (Academics · Experience MIT · Fees & Financial Aid).
// Each block: eyebrow, title, body, image, CTA label + href (internal route or https URL).

import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Info } from "lucide-react";
import api from "@/lib/api";
import FileUploader from "@/components/FileUploader";

const DEFAULT_BLOCKS = [
  { eyebrow: "Academics", title: "", body: "", image_url: "", image_public_id: "", cta_label: "Majors & Programmes", cta_href: "departments" },
  { eyebrow: "Experience MIT", title: "", body: "", image_url: "", image_public_id: "", cta_label: "Schedule a visit", cta_href: "contact" },
  { eyebrow: "Fees & Financial Aid", title: "", body: "", image_url: "", image_public_id: "", cta_label: "Know your fees", cta_href: "admissions" },
];

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

export default function InstituteHomeFeatures() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blocks, setBlocks] = useState(DEFAULT_BLOCKS);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/institute/home-feature-blocks");
        const arr = data?.blocks || [];
        setBlocks(DEFAULT_BLOCKS.map((d, i) => ({ ...d, ...(arr[i] || {}) })));
      } catch {
        // silently keep defaults
      } finally { setLoading(false); }
    })();
  }, []);

  const setAt = (i, patch) => setBlocks((b) => b.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/institute/home-feature-blocks", { blocks });
      toast.success("Home feature sections saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-[#6b655b] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="inst-home-features">
      <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Bulandshahr · Home Page</div>
        <h1 className="font-serif text-2xl text-[#1c1a17]">Home Feature Sections</h1>
        <div className="mt-2 text-sm text-[#6b655b] flex items-start gap-2">
          <Info size={13} className="mt-0.5 shrink-0 text-[#234333]" />
          <span>
            The three editorial blocks rendered between the personalizer and
            testimonials on the public home page. The image here is separate
            from the header hero. CTA link accepts either an internal route
            (like <code className="text-[11px] bg-[#f5f1e8] px-1">admissions</code>) or a full https URL.
          </span>
        </div>
      </div>

      {blocks.map((b, i) => (
        <div
          key={i}
          data-testid={`inst-home-feature-${i}`}
          className="bg-white rounded-sm border border-[#e5e0d8] p-5 space-y-4"
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#c78e42] font-bold">Block {i + 1}</div>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6b655b] font-semibold">Eyebrow</span>
              <input
                type="text"
                value={b.eyebrow || ""}
                onChange={(e) => setAt(i, { eyebrow: e.target.value })}
                maxLength={60}
                className={`${inputCls} mt-1`}
                data-testid={`inst-home-feature-eyebrow-${i}`}
                placeholder="Academics"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6b655b] font-semibold">Title *</span>
              <input
                type="text"
                value={b.title || ""}
                onChange={(e) => setAt(i, { title: e.target.value })}
                maxLength={200}
                className={`${inputCls} mt-1`}
                data-testid={`inst-home-feature-title-${i}`}
                placeholder="For decades, MIT has been a destination for academic rigour."
              />
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6b655b] font-semibold">Body</span>
            <textarea
              value={b.body || ""}
              onChange={(e) => setAt(i, { body: e.target.value })}
              maxLength={2000}
              rows={4}
              className={`${inputCls} mt-1`}
              data-testid={`inst-home-feature-body-${i}`}
              placeholder="Long-form paragraph shown next to the image."
            />
          </label>

          <div>
            <span className="text-[10px] uppercase tracking-widest text-[#6b655b] font-semibold">Image (up to 1 MB, JPG / PNG / WEBP)</span>
            <div className="mt-1">
              <FileUploader
                kind="inst_home_feature_photo"
                label={`block ${i + 1} image`}
                url={b.image_url}
                onChange={({ url, public_id }) => setAt(i, { image_url: url || "", image_public_id: public_id || "" })}
                testid={`inst-home-feature-image-${i}`}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6b655b] font-semibold">CTA Label</span>
              <input
                type="text"
                value={b.cta_label || ""}
                onChange={(e) => setAt(i, { cta_label: e.target.value })}
                maxLength={60}
                className={`${inputCls} mt-1`}
                data-testid={`inst-home-feature-cta-label-${i}`}
                placeholder="Learn more"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6b655b] font-semibold">CTA Link (route or URL)</span>
              <input
                type="text"
                value={b.cta_href || ""}
                onChange={(e) => setAt(i, { cta_href: e.target.value })}
                maxLength={500}
                className={`${inputCls} mt-1 font-mono text-xs`}
                data-testid={`inst-home-feature-cta-href-${i}`}
                placeholder="departments  OR  https://example.com/page"
              />
            </label>
          </div>
        </div>
      ))}

      <div className="sticky bottom-4 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="inst-home-features-save"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3125] disabled:opacity-40 text-white text-[11px] font-bold uppercase tracking-wider px-6 py-3 rounded-sm shadow-lg"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save
        </button>
      </div>
    </div>
  );
}
